/**
 * 사용법
 * ----------------------------------------------------
 * - 폼 내부의 input / select / textarea 값이
 *   "모두" 채워졌을 때 버튼에 특정 클래스를 붙인다.
 *
 * - 생성자: 아무것도 하지 않음
 * - run(): 옵션 주입 + 이벤트 바인딩 + 초기 체크
 *
 * 의존성: jQuery
 *
 * #1. 사용법
 * t.run({
 *   root: '#wrapDiv',     // form 아니어도 OK
 *   button: '#saveBtn',
 *   // fields: 'input[required], select[required]',
 *   // ignore: ':disabled, [type=hidden], [data-skip-check]',
 * });

 *
 * #2. required 필드만 검사
 * t.run({
 *   root: '#wrapDiv',
 *   button: '#saveBtn',
 *   fields: 'input[required], select[required], textarea[required]'
 * });
 *
 * #3. 특정필드 제외
 * <input name="memo" data-skip-check>
 *
 */
/**
 * FillToggle (re-run 지원 버전)
 * ------------------------------------------------------------
 * - run()을 여러 번 호출해도 항상 "최신 옵션"으로 재바인딩된다.
 * - root/fields/ignore가 바뀌어도 정상 동작.
 * - 동적으로 폼이 바뀔 때 run() 재호출로 갱신 가능.
 *
 * 의존성: jQuery
 */
export default class FillToggle {
    constructor() {
        this._running = false;
        this.$root = null;
        this.$button = null;

        this.onClass = 'on';
        this.fields = 'input, select, textarea';
        this.ignore = ':disabled, [type=hidden], [data-skip-check]';
        this.trim = true;

        this.validator = null;     // (el, $el) => boolean
        this.customCheck = null;   // (ctx) => boolean

        this.immediateCheck = true;
        this.requireDirty = false; // true면 값 변경이 있어야 버튼 활성화
        this.selector = null;
        this._snapshot = null;     // requireDirty용 초기값 스냅샷
    }

    run(options = {}) {
        if (this._running) this.destroy();

        this.$root   = options.root instanceof $ ? options.root : $(options.root);
        this.$button = options.button instanceof $ ? options.button : $(options.button);

        this.onClass = options.onClass ?? 'on';
        this.fields  = options.fields ?? 'input, select, textarea';
        this.ignore  = options.ignore ?? ':disabled, [type=hidden], [data-skip-check]';
        this.trim    = options.trim ?? true;

        // 1) 필드 단위 커스텀 체크
        this.validator = typeof options.validator === 'function' ? options.validator : null;

        // 2) 폼(컨테이너) 전체 단위 커스텀 체크
        this.customCheck = typeof options.customCheck === 'function' ? options.customCheck : null;

        this.immediateCheck = options.immediateCheck ?? true;
        this.requireDirty = options.requireDirty ?? false;

        this.selector = `${this.fields}:not(${this.ignore})`;

        // requireDirty: 초기값 스냅샷 저장
        if (this.requireDirty) {
            this._snapshot = this._takeSnapshot();
        }

        this._handler = this.check.bind(this);
        this.$root.on('input.fillToggle change.fillToggle', this.selector, this._handler);

        this._running = true;
        if (this.immediateCheck) this.check();

        return this;
    }

    /**
     * 현재 스냅샷을 갱신한다 (저장 후 dirty 리셋용)
     */
    resetSnapshot() {
        this._snapshot = this._takeSnapshot();
        this.check();
        return this;
    }

    destroy() {
        if (this.$root) this.$root.off('.fillToggle');
        this._running = false;
        this._handler = null;
        this._snapshot = null;
        return this;
    }

    _takeSnapshot() {
        const snap = {};
        this.$root.find(this.selector).each((_, el) => {
            const $el = $(el);
            const key = $el.attr('name') || $el.attr('id') || `__idx_${_}`;
            if ($el.is(':checkbox,:radio')) {
                snap[key] = $el.is(':checked');
            } else {
                snap[key] = String($el.val() ?? '');
            }
        });
        return snap;
    }

    _isDirty() {
        if (!this._snapshot) return true;
        let dirty = false;
        this.$root.find(this.selector).each((_, el) => {
            const $el = $(el);
            const key = $el.attr('name') || $el.attr('id') || `__idx_${_}`;
            let cur;
            if ($el.is(':checkbox,:radio')) {
                cur = $el.is(':checked');
            } else {
                cur = String($el.val() ?? '');
            }
            if (this._snapshot[key] !== cur) {
                dirty = true;
                return false; // break
            }
        });
        return dirty;
    }

    _isFilled(el) {
        const $el = $(el);

        // validator가 있으면 필드 단위 검증을 사용
        if (this.validator) return !!this.validator(el, $el, this);

        // 기본 규칙
        if ($el.is(':checkbox,:radio')) return $el.is(':checked');

        let val = $el.val();
        if (val == null) return false;
        if (Array.isArray(val)) return val.length > 0;

        val = String(val);
        if (this.trim) val = val.trim();

        return val !== '';
    }

    check() {
        if (!this.$root || !this.selector || !this.$button) return false;

        let filled = true;

        // 1) 필드들이 모두 채워졌는지 검사
        const $fields = this.$root.find(this.selector);
        $fields.each((_, el) => {
            if (!this._isFilled(el)) {
                filled = false;
                return false; // break
            }
        });

        // 2) 모두 채워졌다면, 추가 조건(customCheck)도 검사
        if (filled && this.customCheck) {
            const ctx = {
                root: this.$root,
                button: this.$button,
                fields: $fields,
                instance: this
            };
            filled = !!this.customCheck(ctx);
        }

        // 3) requireDirty: 변경이 있어야 활성화
        if (filled && this.requireDirty) {
            filled = this._isDirty();
        }

        this.$button.toggleClass(this.onClass, filled);
        return filled;
    }
}

